import React, { FC, useCallback, useEffect, useState } from 'react';
import firebase from 'firebase/app';
import { adminApi } from 'util/adminApi';
import { ui } from 'util/ui';
import { Panel, SectionHead, TRow, Cell, AdminButton, Pill, Notice } from './adminUi';

/**
 * Activity tab: every change to the library and every admin action, newest first,
 * recorded by the server. Deleted records can be put back from here.
 */

type Entry = {
  id: string;
  at: Date | null;
  kind: 'data' | 'admin';
  action: string;
  collection?: string;
  docId?: string;
  title?: string | null;
  changedFields?: string[];
  actor?: { type: string; id: string | null; email?: string | null };
  targetEmail?: string | null;
  from?: string | null;
  to?: string | null;
  restoredAt?: unknown;
  before?: { _omitted?: boolean } | null;
};

const PAGE = 100;
const SINGULAR: Record<string, string> = {
  projects: 'project',
  records: 'record',
  videos: 'video',
  notes: 'note',
  assets: 'asset',
  gallery: 'gallery item',
  images: 'image',
  categories: 'category',
  defaults: 'defaults',
};

const tierName = (cls?: string | null) => (cls === 'ARCHIVE' ? 'cold storage' : cls === 'STANDARD' ? 'hot storage' : cls || '?');

const describe = (e: Entry): string => {
  // Entries written by the server itself (storage tiers, 5 Nov 2026).
  if ((e.kind as string) === 'system' || e.action.startsWith('storage.')) {
    const x = e as Entry & { summary?: { moved?: number }; moved?: number; queued?: number; checked?: number; result?: string };
    switch (e.action) {
      case 'storage.tier':
        return `Moved video “${e.docId}” to ${tierName(e.to)}${x.result === 'check' ? ' (needs a check)' : ''}`;
      case 'storage.tierScan':
        return `Daily storage check: ${x.queued ?? 0} file(s) queued to move (of ${x.checked ?? 0} checked)`;
      case 'storage.tiers':
        return `Storage fix: ${x.summary?.moved ?? x.moved ?? 0} file(s) moved to the right tier`;
      default:
        return e.action;
    }
  }
  if (e.kind === 'admin') {
    const who = e.targetEmail || e.docId || '';
    switch (e.action) {
      case 'user.create':
        return `Created account ${who} (${e.to})`;
      case 'user.disable':
        return `Disabled ${who}`;
      case 'user.enable':
        return `Enabled ${who}`;
      case 'user.setRole':
        return `Changed ${who} from ${e.from || 'no access'} to ${e.to || 'no access'}`;
      case 'user.delete':
        return `Deleted the sign-in account ${who}`;
      case 'user.resetLink':
        return `Made a password reset link for ${who}`;
      case 'record.restore':
        return `Restored ${SINGULAR[e.collection || ''] || e.collection} ${e.docId}`;
      default:
        return e.action;
    }
  }
  const thing = `${SINGULAR[e.collection || ''] || e.collection} “${e.title || e.docId}”`;
  if (e.action === 'create') return `Added ${thing}`;
  if (e.action === 'delete') return `Deleted ${thing}`;
  return `Edited ${thing}${e.changedFields?.length ? ` (${e.changedFields.join(', ')})` : ''}`;
};

const ActivityLog: FC = () => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [emails, setEmails] = useState<Record<string, string>>({});
  const [onlyDeletes, setOnlyDeletes] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = firebase.firestore().collection('activity').orderBy('at', 'desc').limit(PAGE);
      if (onlyDeletes) {
        q = firebase.firestore().collection('activity').where('action', '==', 'delete').orderBy('at', 'desc').limit(PAGE);
      }
      const snap = await q.get();
      setEntries(
        snap.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, at: data.at ? data.at.toDate() : null } as Entry;
        }),
      );
      const users = await adminApi.listUsers();
      setEmails(Object.fromEntries(users.users.map((u) => [u.uid, u.email || u.uid])));
      setMessage(null);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onlyDeletes]);

  useEffect(() => {
    load();
  }, [load]);

  const restore = async (e: Entry) => {
    if (!window.confirm(`Put back ${describe(e).replace(/^Deleted /, '')} exactly as it was before it was deleted?`)) return;
    setBusy(e.id);
    try {
      const res = await adminApi.restoreDeleted(e.id);
      setMessage(`Restored ${res.restored}.${res.note ? ' ' + res.note : ''}`);
      await load();
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const who = (e: Entry) => {
    if (!e.actor) return '';
    if (e.actor.email) return e.actor.email;
    if (e.actor.id && emails[e.actor.id]) return emails[e.actor.id];
    if (e.actor.type === 'service_account' || e.actor.type === 'system') return 'System';
    return e.actor.id || e.actor.type;
  };

  const COLS = '150px minmax(0,3fr) minmax(0,1.5fr) 110px';
  return (
    <section style={{ marginBottom: 28 }}>
      <SectionHead title="Activity" note={`Latest ${PAGE} changes, recorded by the server. Nobody can edit or remove this log from the app.`}>
        <div role="group" aria-label="Show" style={{ display: 'inline-grid', gridTemplateColumns: 'repeat(2, auto)', gap: 4, padding: 4, background: ui.subtle, borderRadius: 12 }}>
          {[false, true].map((del) => {
            const on = onlyDeletes === del;
            return (
              <button
                key={String(del)}
                type="button"
                aria-pressed={on}
                onClick={() => setOnlyDeletes(del)}
                style={{
                  height: 32,
                  padding: '0 14px',
                  border: 'none',
                  borderRadius: 9,
                  background: on ? ui.surface : 'transparent',
                  boxShadow: on ? '0 1px 3px rgba(0,0,0,0.14)' : 'none',
                  color: on ? ui.ink : ui.muted,
                  fontFamily: ui.font,
                  fontSize: 13,
                  fontWeight: on ? 600 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {del ? 'Deletions only' : 'All changes'}
              </button>
            );
          })}
        </div>
      </SectionHead>
      {message && <Notice>{message}</Notice>}
      <Panel>
        <TRow head columns={COLS}>
          <Cell>When</Cell>
          <Cell>What</Cell>
          <Cell>Who</Cell>
          <Cell />
        </TRow>
        {loading && !entries.length && <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: ui.muted }}>Loading…</p>}
        {!loading && !entries.length && (
          <p style={{ margin: 0, padding: '14px 16px', fontSize: 14, color: ui.muted }}>
            Nothing recorded yet. Changes appear here from the moment the activity log was switched on.
          </p>
        )}
        {entries.map((e) => (
          <TRow key={e.id} columns={COLS}>
            <Cell muted>{e.at ? e.at.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' } as any) : '…'}</Cell>
            <Cell>{describe(e)}</Cell>
            <Cell muted label="Who" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {who(e)}
            </Cell>
            <Cell>
              {e.kind === 'data' && e.action === 'delete' && !e.restoredAt && e.before && !e.before._omitted && (
                <AdminButton tone="primary" onClick={() => restore(e)} disabled={busy !== null}>
                  {busy === e.id ? 'Restoring…' : 'Restore'}
                </AdminButton>
              )}
              {!!e.restoredAt && <Pill tone="green">Restored</Pill>}
            </Cell>
          </TRow>
        ))}
      </Panel>
    </section>
  );
};

export default ActivityLog;
